import { updateVehicle, getVehicle } from '@api/vehicles/vehicles.service';
import { ResponseErrorDto, VehicleCategory } from '@interfaces/models';
import { useMutation, useQuery } from '@tanstack/react-query';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { zodResolver } from '@hookform/resolvers/zod';
import { useGetCostCenters } from '@shared/hooks';
import { useTranslation } from 'react-i18next';
import { Validators } from '@helpers/constant';
import { useForm } from 'react-hook-form';
import { toast } from '@ui/components';
import { TFunction } from 'i18next';
import { useEffect } from 'react';
import { z } from 'zod';

import { useVehicleAssignEmployees } from '../vehicle-assign-employees/use-vehicle-assign-employeess';

const vehicleFormSchema = (t: TFunction) =>
  z.object({
    registrationNumber: z
      .string()
      .trim()
      .min(1, { message: t('FORMS.REQUIRED') })
      .regex(Validators.REGISTRATION_NUMBER, t('FORMS.INVALID_REGISTRATION_NUMBER')),
    name: z
      .string()
      .trim()
      .min(2, { message: t('FORMS.MIN_CHARACTERS', { limit: 2 }) })
      .max(20, {
        message: t('FORMS.MAX_CHARACTERS', { limit: 20 }),
      })
      .or(z.literal('')),
    costCenters: z.array(z.string()).min(1, {
      message: t('FORMS.REQUIRED'),
    }),
    registrationCountry: z.string().min(1, { message: t('FORMS.REQUIRED') }),
    vehicleCategory: z.string().min(1, { message: t('FORMS.REQUIRED') }),
    assignedEmployees: z.array(z.string()).optional(),
  });

type VehicleFormData = z.infer<ReturnType<typeof vehicleFormSchema>>;

const DEFAULT_VALUES: VehicleFormData = {
  registrationCountry: 'PL',
  registrationNumber: '',
  vehicleCategory: '',
  costCenters: [],
  name: '',
  assignedEmployees: [],
};

export const useEditVehicle = ({
  onSuccess,
  vehicleId,
}: {
  onSuccess?: () => void;
  vehicleId: string;
}) => {
  const { t } = useTranslation();
  const {
    costCentersOptions: costCenterOptions,
    isFetching: isCostCenterLoading,
    automaticAssignmentCostCenter,
  } = useGetCostCenters();

  const { data: vehicle, isLoading: isVehicleLoading } = useQuery({
    queryKey: ['vehicle', vehicleId],
    queryFn: () => getVehicle(vehicleId),
  });

  const form = useForm<VehicleFormData>({
    resolver: zodResolver(vehicleFormSchema(t)),
    defaultValues: DEFAULT_VALUES,
  });

  const assignedEmployees = form.watch('assignedEmployees');
  const assignTable = useVehicleAssignEmployees(assignedEmployees ?? []);

  const { mutate: updateVehicleMutation, isPending: isUpdateVehiclePending } = useMutation({
    mutationFn: (data: VehicleFormData) =>
      updateVehicle({
        id: vehicleId,
        data: {
          ...data,
          id: vehicleId,
          vehicleCategory: data.vehicleCategory as VehicleCategory,
        },
      }),
    onSuccess: () => {
      toast.success(t('VEHICLES.UPDATE_VEHICLE_SUCCESS'));
      onSuccess?.();
    },
    onError: ({ error }: { error: ResponseErrorDto }) => {
      toast.error(t(`API_ERRORS.${error.errorCode}`));
    },
  });

  const updateVehicleHandler = (data: VehicleFormData) => {
    updateVehicleMutation(data);
  };

  const onSubmit = form.handleSubmit(updateVehicleHandler);

  useEffect(() => {
    if (vehicle) {
      form.reset(vehicle);
    }
  }, [vehicle, form]);

  const selectedCostCenters = form.watch('costCenters');

  useDeepCompareEffect(() => {
    if (!selectedCostCenters.length && automaticAssignmentCostCenter) {
      form.setValue('costCenters', [automaticAssignmentCostCenter.id]);
    }
  }, [automaticAssignmentCostCenter, selectedCostCenters]);

  return {
    isLoading: isCostCenterLoading || isVehicleLoading,
    isUpdateVehiclePending,
    costCenterOptions,
    assignedEmployees,
    assignTable,
    onSubmit,
    form,
    t,
  };
};
